import type {Opportunity} from '../types'

export const ATTRIBUTED_REF_PATTERN=/^aco_[A-Za-z0-9_-]{43}$/
export const CHECKOUT_WINDOW_MS=30*60*1000
export const OPEN_OPPORTUNITY_STAGES=Object.freeze(['discovery','qualified','proposal','decision'] as const)
export type CanonicalCheckoutPlan='watch'|'protect'|'shield'
export type ProviderCapabilityState='AVAILABLE'|'PROVIDER_PENDING'
export type PaymentDeskSession={sessionId?:string;attributionRef:string;expiresAt:string;canonicalPlanId:CanonicalCheckoutPlan;providerState:ProviderCapabilityState}
export type ProviderTakePaymentConfig={takePaymentEnabled?:unknown;takePaymentProvider?:unknown}|null|undefined

const UUID=/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const EMAIL=/^[^\s@]+@[^\s@]+$/
const PLANS:readonly CanonicalCheckoutPlan[]=['watch','protect','shield']

export function isValidAttributionRef(value:string){return ATTRIBUTED_REF_PATTERN.test(value)}

export function buildAttributedCheckoutUrl(origin:string,ref:string){
 if(!isValidAttributionRef(ref))throw new Error('invalid_attribution_reference')
 return `${origin.replace(/\/+$/,'')}/?attribution_ref=${encodeURIComponent(ref)}`
}

export function extractAttributionRef(checkoutUrl:string){
 try{const value=new URL(checkoutUrl).searchParams.get('attribution_ref')||'';return isValidAttributionRef(value)?value:null}catch{return null}
}

export function providerCapabilityState(config:ProviderTakePaymentConfig=undefined):ProviderCapabilityState{
 return config?.takePaymentEnabled===true&&typeof config.takePaymentProvider==='string'&&config.takePaymentProvider.trim().length>0?'AVAILABLE':'PROVIDER_PENDING'
}

export function canWalkToCheckout(opportunity:Pick<Opportunity,'stage'>|null|undefined,contactEmail?:string|null){
 return walkToCheckoutBlocker(opportunity,contactEmail)===null
}

export function hasReachableContactEmail(contactEmail?:string|null){
 return typeof contactEmail==='string'&&EMAIL.test(contactEmail.trim())
}

export function walkToCheckoutBlocker(opportunity:Pick<Opportunity,'stage'>|null|undefined,contactEmail?:string|null):string|null{
 if(!opportunity)return 'opportunity_required'
 if(!(OPEN_OPPORTUNITY_STAGES as readonly string[]).includes(opportunity.stage))return 'opportunity_stage_closed'
 if(!hasReachableContactEmail(contactEmail))return 'contact_email_unreachable'
 return null
}

export function redactForDisplay(session:{attributionRef:string}){
 return{attributionRef:`••••${session.attributionRef.slice(-4)}`}
}

export function millisecondsRemaining(expiresAt:string,nowMs:number){const target=Date.parse(expiresAt);return Number.isNaN(target)?0:target-nowMs}
export function secondsRemaining(expiresAt:string,nowMs:number){return Math.max(0,Math.ceil(millisecondsRemaining(expiresAt,nowMs)/1000))}
export function sessionExpiryState(expiresAt:string,nowMs:number):'ACTIVE'|'EXPIRED'{return millisecondsRemaining(expiresAt,nowMs)>0?'ACTIVE':'EXPIRED'}

export type AttributedCheckoutIssueInput={organizationId:string;contactId:string;opportunityId:string;planId:CanonicalCheckoutPlan;locationCount:number;idempotencyKey:string;approvalId?:string;termsHash?:string;canonicalTerms?:string;callSessionId?:string}

export function buildAttributedCheckoutRequest(input:AttributedCheckoutIssueInput){
 const valid=UUID.test(input.organizationId)&&UUID.test(input.contactId)&&UUID.test(input.opportunityId)&&PLANS.includes(input.planId)&&Number.isInteger(input.locationCount)&&input.locationCount>=1&&input.locationCount<=10000&&typeof input.idempotencyKey==='string'&&input.idempotencyKey.length>=16&&input.idempotencyKey.length<=128
 if(!valid)throw new Error('invalid_checkout_request')
 const body:{contactId:string;idempotencyKey:string;locationCount:number;opportunityId:string;organizationId:string;planId:CanonicalCheckoutPlan;approvalId?:string;termsHash?:string;canonicalTerms?:string;callSessionId?:string}={contactId:input.contactId,idempotencyKey:input.idempotencyKey,locationCount:input.locationCount,opportunityId:input.opportunityId,organizationId:input.organizationId,planId:input.planId}
 if(input.approvalId!==undefined)body.approvalId=input.approvalId
 if(input.termsHash!==undefined)body.termsHash=input.termsHash
 if(input.canonicalTerms!==undefined)body.canonicalTerms=input.canonicalTerms
 if(input.callSessionId!==undefined)body.callSessionId=input.callSessionId
 return{path:'/api/attributed-checkout',body}
}

export type CheckoutAttemptStatus='IDLE'|'ISSUING'|'ISSUED'|'FAILED'
export type CheckoutAttempt={idempotencyKey:string;status:CheckoutAttemptStatus;attempts:number}
export function createCheckoutAttempt(idempotencyKey:string):CheckoutAttempt{return{idempotencyKey,status:'IDLE',attempts:0}}
export function beginCheckoutAttempt(attempt:CheckoutAttempt):CheckoutAttempt{if(attempt.status==='ISSUING'||attempt.status==='ISSUED')return attempt;return{idempotencyKey:attempt.idempotencyKey,status:'ISSUING',attempts:attempt.attempts+1}}
export function completeCheckoutAttempt(attempt:CheckoutAttempt):CheckoutAttempt{return attempt.status==='ISSUING'?{...attempt,status:'ISSUED'}:attempt}
export function failCheckoutAttempt(attempt:CheckoutAttempt):CheckoutAttempt{return attempt.status==='ISSUING'?{idempotencyKey:attempt.idempotencyKey,status:'FAILED',attempts:attempt.attempts}:attempt}
export function resetCheckoutAttempt(nextIdempotencyKey:string):CheckoutAttempt{return createCheckoutAttempt(nextIdempotencyKey)}
